// Component/Workshop/SLA/CaseSLAPanel.jsx
// Shows per-case SLA rule statuses, manual trigger buttons, and email log.
// Renders as the right column inside the Timeline tab.

import { useState } from "react";
import {
  useCaseSLA,
  useCaseNotifications,
  useTriggerSLA,
  useSendEventEmail,
} from "../../../hooks/useWorkshopQueries";
import { Spinner, EmptyState, Modal, formatDateTime, timeAgo } from "../UI/index";

// ── Status display config ─────────────────────────────────────────────────────
const SLA_STATUS_CONFIG = {
  ON_TRACK:          { color: "#22c55e", bg: "rgba(34,197,94,.12)",   label: "On Track"     },
  BREACHED:          { color: "#ef4444", bg: "rgba(239,68,68,.12)",   label: "Breached"     },
  COMPLETED_ON_TIME: { color: "#14b8a6", bg: "rgba(20,184,166,.12)",  label: "Done On Time" },
  COMPLETED_LATE:    { color: "#f97316", bg: "rgba(249,115,22,.12)",  label: "Done Late"    },
};

const NOTIF_TYPE_CONFIG = {
  AUTO:   { color: "#a855f7", bg: "rgba(168,85,247,.15)", label: "Auto"   },
  MANUAL: { color: "#3b82f6", bg: "rgba(59,130,246,.15)", label: "Manual" },
  EVENT:  { color: "#14b8a6", bg: "rgba(20,184,166,.15)", label: "Event"  },
};

// Event email templates the admin can manually send
const EVENT_TEMPLATES = [
  { key: "caseRegistered",               label: "Case Registered",            roles: ["ADMIN", "CUSTOMER"] },
  { key: "claimFormToInsurer",           label: "Claim Form to Insurer",      roles: ["INSURANCE", "ADMIN"] },
  { key: "surveyorAssigned",             label: "Surveyor Assigned (Customer)",roles: ["CUSTOMER"] },
  { key: "surveyorAssignedNotification", label: "Surveyor Assigned (Surveyor)",roles: ["SURVEYOR"] },
  { key: "workshopSurveyCompleted",      label: "Workshop Survey Completed",   roles: ["ADMIN"] },
];

// ── Main component ────────────────────────────────────────────────────────────
export default function CaseSLAPanel({ caseId }) {
  const [triggerModal, setTriggerModal] = useState(null); // { ruleCode, description }
  const [eventModal,   setEventModal]   = useState(false);
  const [eventTemplate, setEventTemplate] = useState("");
  const [eventRoles,    setEventRoles]    = useState([]);
  const [sending,       setSending]       = useState(false);
  const [toast,         setToast]         = useState(null);

  const { data: slaData,   isLoading: slaLoading   } = useCaseSLA(caseId);
  const { data: notifData, isLoading: notifLoading } = useCaseNotifications(caseId);
  const triggerSLA  = useTriggerSLA();
  const sendEvent   = useSendEventEmail();

  function showToast(msg, ok = true) {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  }

  function closeEventModal() {
    setEventModal(false);
    setEventTemplate("");
    setEventRoles([]);
  }

  async function handleManualTrigger() {
    if (!triggerModal) return;
    setSending(true);
    try {
      await triggerSLA.mutateAsync({ caseId, ruleCode: triggerModal.ruleCode });
      showToast(`Notification sent for ${triggerModal.ruleCode.replace(/_/g, " ")}`);
      setTriggerModal(null);
    } catch (err) {
      showToast(err?.response?.data?.message || "Trigger failed", false);
    } finally {
      setSending(false);
    }
  }

  async function handleSendEvent() {
    if (!eventTemplate) return;
    const tpl = EVENT_TEMPLATES.find(t => t.key === eventTemplate);
    const roles = eventRoles.length ? eventRoles : (tpl?.roles || []);
    setSending(true);
    try {
      await sendEvent.mutateAsync({ caseId, templateKey: eventTemplate, recipientRoles: roles });
      showToast(`Email sent: ${tpl?.label || eventTemplate}`);
      closeEventModal();
    } catch (err) {
      showToast(err?.response?.data?.message || "Send failed", false);
    } finally {
      setSending(false);
    }
  }

  const rules     = slaData?.rules        || [];
  const notifList = notifData?.notifications || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 2000,
          background: toast.ok ? "#22c55e" : "#ef4444", color: "#fff",
          padding: "10px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600,
          boxShadow: "0 4px 20px rgba(0,0,0,.4)", pointerEvents: "none",
        }}>{toast.msg}</div>
      )}

      {/* ── SLA Monitor ── */}
      <div className="wd-card" style={{ padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--wd-t1)" }}>SLA Monitor</div>
          {slaData && (
            <span style={{ fontSize: 11, color: "var(--wd-t3)" }}>
              Priority: <strong style={{ color: "var(--wd-t2)" }}>{slaData.priority}</strong>
              {" "}· ×{slaData.multiplier} scale
            </span>
          )}
        </div>

        {slaLoading ? <Spinner /> :
         rules.length === 0 ? <EmptyState message="No SLA rules active for this case" /> : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {rules.map(rule => {
              const cfg = SLA_STATUS_CONFIG[rule.status] || SLA_STATUS_CONFIG.ON_TRACK;
              const elapsed = (Date.now() - new Date(rule.startedAt)) / (rule.effectiveSlaHours * 3600000);
              const pct = Math.min(100, Math.round(elapsed * 100));

              return (
                <div key={rule.ruleCode} style={{
                  border: `1px solid ${cfg.color}44`, borderRadius: 8,
                  padding: "10px 12px", background: cfg.bg,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Header row */}
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <span style={{
                          fontSize: 10, fontWeight: 800, padding: "2px 7px", borderRadius: 10,
                          background: `${cfg.color}22`, color: cfg.color, border: `1px solid ${cfg.color}55`,
                        }}>{cfg.label}</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--wd-t1)" }}>
                          {rule.ruleCode.replace(/_/g, " ")}
                        </span>
                      </div>

                      {/* Description */}
                      <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 3, lineHeight: 1.5 }}>
                        {rule.description}
                      </div>

                      {/* Meta row */}
                      <div style={{ fontSize: 10, color: "var(--wd-t3)", marginTop: 4 }}>
                        SLA: {rule.effectiveSlaHours}h · Deadline: {formatDateTime(rule.deadline)}
                        {rule.notificationsSent > 0 && ` · ${rule.notificationsSent} email(s) sent`}
                      </div>

                      {/* Progress bar */}
                      <div style={{ marginTop: 6, height: 3, borderRadius: 2, background: "rgba(148,163,184,.2)", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${pct}%`, background: cfg.color, borderRadius: 2, transition: "width .5s" }} />
                      </div>
                    </div>

                    {/* Trigger button — only shown when breached and canManualTrigger */}
                    {rule.canManualTrigger && (
                      <button
                        className="wd-btn wd-btn-ghost wd-btn-sm"
                        style={{ flexShrink: 0, borderColor: cfg.color, color: cfg.color, fontSize: 11, padding: "4px 10px" }}
                        onClick={() => setTriggerModal({ ruleCode: rule.ruleCode, description: rule.description })}
                      >
                        ⚡ Trigger
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Send Event Email ── */}
      <div className="wd-card" style={{ padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--wd-t1)" }}>Manual Email</div>
          <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 2 }}>Re-send a notification for this case</div>
        </div>
        <button className="wd-btn wd-btn-primary wd-btn-sm" onClick={() => setEventModal(true)}>
          ✉ Send Email
        </button>
      </div>

      {/* ── Email Log ── */}
      <div className="wd-card" style={{ padding: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--wd-t1)", marginBottom: 12 }}>Email Log</div>

        {notifLoading ? <Spinner /> :
         notifList.length === 0 ? <EmptyState message="No emails sent for this case yet" /> : (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {notifList.map((n, i) => {
              const tc = NOTIF_TYPE_CONFIG[n.notificationType] || NOTIF_TYPE_CONFIG.AUTO;
              return (
                <div key={n.id || i} style={{
                  display: "flex", gap: 10, alignItems: "flex-start",
                  padding: "8px 0",
                  borderBottom: i < notifList.length - 1 ? "1px solid var(--wd-border)" : "none",
                }}>
                  {/* Type badge */}
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 10,
                    background: tc.bg, color: tc.color, border: `1px solid ${tc.color}44`,
                    flexShrink: 0, marginTop: 2,
                  }}>{tc.label}</span>

                  {/* Middle: subject + recipient */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--wd-t1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {n.subject || n.ruleCode?.replace(/_/g, " ") || "—"}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 2 }}>
                      To: {n.sentTo || "—"}{n.recipientRole ? ` (${n.recipientRole})` : ""}
                    </div>
                  </div>

                  {/* Right: status + time */}
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 6,
                      background: n.emailStatus === "SENT" ? "rgba(34,197,94,.15)" : "rgba(239,68,68,.15)",
                      color: n.emailStatus === "SENT" ? "#22c55e" : "#ef4444",
                    }}>{n.emailStatus}</span>
                    <div style={{ fontSize: 10, color: "var(--wd-t3)", marginTop: 3 }}>
                      {timeAgo(n.createdAt)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Manual SLA Trigger modal ── */}
      <Modal
        open={!!triggerModal}
        onClose={() => setTriggerModal(null)}
        title="Manual SLA Notification"
        footer={<>
          <button className="wd-btn wd-btn-ghost" onClick={() => setTriggerModal(null)}>Cancel</button>
          <button className="wd-btn wd-btn-primary" onClick={handleManualTrigger} disabled={sending}>
            {sending ? "Sending…" : "Send Now"}
          </button>
        </>}
      >
        <p style={{ fontSize: 14, color: "var(--wd-t2)", lineHeight: 1.6 }}>
          Trigger the{" "}
          <strong style={{ color: "var(--wd-t1)" }}>{triggerModal?.ruleCode?.replace(/_/g, " ")}</strong>
          {" "}notification manually?
        </p>
        <p style={{ fontSize: 12, color: "var(--wd-t3)", marginTop: 6, lineHeight: 1.6 }}>
          {triggerModal?.description}
        </p>
        <p style={{ fontSize: 12, color: "var(--wd-orange)", marginTop: 10, fontWeight: 600 }}>
          ⚠ An email will be sent to the assigned roles immediately.
        </p>
      </Modal>

      {/* ── Send Event Email modal ── */}
      <Modal
        open={eventModal}
        onClose={closeEventModal}
        title="Send Event Email"
        footer={<>
          <button className="wd-btn wd-btn-ghost" onClick={closeEventModal}>Cancel</button>
          <button className="wd-btn wd-btn-primary" onClick={handleSendEvent} disabled={!eventTemplate || sending}>
            {sending ? "Sending…" : "Send Email"}
          </button>
        </>}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label className="wd-form-label">Email Template *</label>
            <select
              className="wd-input wd-select"
              value={eventTemplate}
              onChange={e => {
                setEventTemplate(e.target.value);
                const tpl = EVENT_TEMPLATES.find(t => t.key === e.target.value);
                setEventRoles(tpl?.roles || []);
              }}
            >
              <option value="">Select template…</option>
              {EVENT_TEMPLATES.map(t => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
          </div>

          {eventTemplate && (
            <div>
              <label className="wd-form-label">Will be sent to</label>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                {eventRoles.map(r => (
                  <span key={r} style={{
                    fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 12,
                    background: "rgba(59,130,246,.15)", color: "#3b82f6",
                    border: "1px solid rgba(59,130,246,.3)",
                  }}>{r}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
